import { computed, h, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { NButton, NPopover } from 'naive-ui'
import { useAuthz } from '../../../../composables/useAuthz'
import { AppSvgEIcon, SvgIcon } from '@/components/common'
import { useClassroomStore, useFavoritesStore, useUserStore } from '@/store'
import { t } from '@/locales'
import { useWorkspaceFolderStore } from '@/store/modules/folder'
import ActionFolder from '@/views/folder/ActionFolder.vue'
import { getExpandedMenuKeys, getExpandedMenuKeysStudent, getExpandedMenuKeysTeacher } from '@/utils/routeHelper'
import { RolePermission } from '@/constants/role'
import { useNavigation } from '@/components/layout/sider/composables/useNavigation'

export function useMenuItems(collapsed: any, handleAdd: () => void) {
  const route = useRoute()
  const router = useRouter()
  const userStore = useUserStore()
  const workspaceFolderStore = useWorkspaceFolderStore()

  // Track dropdown states for each folder
  const folderDropdownStates = ref<Record<number, boolean>>({})

  const handleDropdownChange = (folderId: number, isOpen: boolean) => {
    folderDropdownStates.value[folderId] = isOpen
  }

  const workSpaceName = computed(() => userStore.workSpace)
  const listWorkSpaceFolder = computed(() => workspaceFolderStore.getFolders)
  const listFolder = computed(() => listWorkSpaceFolder.value || [])
  const userRole = computed(() => userStore.role)
  const { isOwner, has, hasFolder } = useAuthz(userRole)

  const expandedKeys = computed(() => {
    return getExpandedMenuKeys(route, listWorkSpaceFolder.value)
  })

  // Helper function để check permission cho menu items
  const hasMenuPermission = (permission: RolePermission) => {
    return isOwner.value || has(permission)
  }

  // Base menu items
  const getChatMenuItem = () => ({
    label: t('menu.chat'),
    icon: () => h(SvgIcon, { icon: 'ri:chat-poll-line', size: '20' }),
    key: 'chat-home',
  })

  const getDivider = (key: string) => ({
    type: 'divider',
    key,
  })

  const getGroupHeader = (labelKey: string, key: string) => {
    if (collapsed.value)
      return null
    return {
      type: 'group',
      label: () => h('span', { class: 'font-bold text-gray-500 dark:text-gray-400' }, t(labelKey)),
      key,
    }
  }

  // Agent section
  const getAgentItems = () => {
    const items = []

    if (isOwner.value
			|| (hasMenuPermission(RolePermission.FOLDER_VIEW)
				|| hasMenuPermission(RolePermission.KNOWLEDGE_VIEW)
				|| hasMenuPermission(RolePermission.TASK_VIEW))
    ) {
      const groupHeader = getGroupHeader('menu.group.agent', 'agent-group')
      if (groupHeader)
        items.push(groupHeader)
    }

    // Luôn hiển thị workspace menu nếu có quyền FOLDER.VIEW hoặc là owner
    if (userRole.value?.is_owner === true || hasMenuPermission(RolePermission.FOLDER_VIEW)) {
      items.push({
        label: () => h('div', {
          class: 'flex items-center justify-between w-full min-w-0',
          onClick: (e: Event) => {
            e.stopPropagation()
            router.push('/agent-management')
          },
        }, [
          h('span', {
            class: 'flex-1 min-w-0 truncate mr-2',
            title: workSpaceName.value ?? '',
          }, workSpaceName.value ?? ''),
          // Chỉ hiển thị button thêm folder nếu có quyền FOLDER.EDIT
          hasMenuPermission(RolePermission.FOLDER_EDIT) && h(NButton, {
            text: true,
            class: 'flex-shrink-0',
            onClick: (e: Event) => {
              e.stopPropagation()
              handleAdd()
            },
          }, () => h(SvgIcon, { icon: 'ri:add-circle-line', size: '20' })),
        ]),
        icon: () => h(SvgIcon, { icon: 'ri:folders-line', size: '20' }),
        key: 'workspace',
        children: listFolder.value.map((folder: any) => ({
          label: () => h('div', { class: 'flex items-center justify-between w-full group min-w-0' }, [
            h('div', { class: 'flex items-center gap-2 flex-1 min-w-0' }, [
              h(SvgIcon, { icon: 'ri:folder-line', size: '20', class: 'flex-shrink-0' }),
              h('span', {
                class: 'truncate',
                title: folder.name,
              }, folder.name),
            ]),
            h('div', { class: 'flex items-center flex-shrink-0' }, [
              folder.isPinned && h('div', { class: 'w-[20px] h-[20px] flex justify-center items-center' }, [
                h(SvgIcon, { icon: 'ri:bookmark-3-line', size: '18' }),
              ]),
              // Chỉ hiển thị ActionFolder nếu có quyền FOLDER.EDIT hoặc là owner
              (isOwner.value || hasFolder(RolePermission.FOLDER_EDIT, folder.id)) && h('div', {
                class: folderDropdownStates.value[folder.id] ? 'block' : 'hidden group-hover:block',
                onClick: (e: Event) => e.stopPropagation(),
              }, [
                h(ActionFolder, {
                  isOrder: folder.isPinned,
                  uuid: folder.id,
                  showDelete: !folder?.folder_default_classroom_agent,
                  onDropdownChange: (isOpen: boolean) => handleDropdownChange(folder.id, isOpen),
                }),
              ]),
            ]),
          ]),
          key: `folder-${folder.id}`,
        })),
      })
    }

    // Knowledge menu - kiểm tra permission KNOWLEDGE.VIEW
    if (userRole.value?.is_owner === true || hasMenuPermission(RolePermission.KNOWLEDGE_VIEW))
      items.push({ label: t('menu.knowledges'), icon: () => h(SvgIcon, { icon: 'ri:book-3-line', size: '20' }), key: 'knowledge' })

    // Tasks menu - kiểm tra permission TASK.VIEW
    if (userRole.value?.is_owner === true || hasMenuPermission(RolePermission.TASK_VIEW))
      items.push({ label: t('menu.tasks'), icon: () => h(SvgIcon, { icon: 'ri:briefcase-line', size: '20' }), key: 'tasks' })

    return items
  }

  // store section
  const getStoreItems = () => {
    const items = []

    const groupHeader = getGroupHeader('menu.group.store', 'store-group')
    if (groupHeader)
      items.push(groupHeader)

    // Agent store
    items.push({ label: t('menu.agentStore'), icon: () => h(SvgIcon, { icon: 'ri:store-2-line', size: '20' }), key: 'agent-store' })

    // Transaction
    items.push({ label: t('menu.transaction'), icon: () => h(SvgIcon, { icon: 'ri:wallet-3-line', size: '20' }), key: 'transaction' })

    return items
  }

  // school section
  const getClassroomItems = () => {
    const items = []

    const groupHeader = getGroupHeader('menu.group.school', 'school-group')
    if (groupHeader)
      items.push(groupHeader)

    items.push(
      { label: t('menu.school'), icon: () => h(SvgIcon, { icon: 'ri:school-line', size: '20' }), key: 'school' },
      { label: t('menu.classroom'), icon: () => h(SvgIcon, { icon: 'ri:team-line', size: '20' }), key: 'classroom-v2' },

    )

    return items
  }

  // Base tools
  const getBaseToolItems = () => {
    const items = []

    const groupHeader = getGroupHeader('menu.group.tool', 'tool-group')
    if (groupHeader)
      items.push(groupHeader)

    items.push(
      // { label: t('menu.imageGeneration'), icon: () => h(SvgIcon, { icon: 'ri:multi-image-line', size: '20' }), key: 'image-generation' },
      { label: t('menu.slide'), icon: () => h(SvgIcon, { icon: 'ri:slideshow-line', size: '20' }), key: 'slide' },
      { label: t('menu.writer'), icon: () => h(SvgIcon, { icon: 'ri:brush-line', size: '20' }), key: 'writer' },
      { label: t('menu.whiteboard'), icon: () => h(SvgIcon, { icon: 'ri:draft-line', size: '20' }), key: 'Whiteboard' },
      { label: t('menu.chatPdf'), icon: () => h(SvgIcon, { icon: 'ri:file-pdf-line', size: '20' }), key: 'chat-pdf' },
      // { label: t('menu.mindmap'), icon: () => h(SvgIcon, { icon: 'ri:mind-map', size: '20' }), key: 'mind-map' },
      // { label: t('menu.imageToWord'), icon: () => h(SvgIcon, { icon: 'ri:image-line', size: '20' }), key: 'image-to-word' },
      // { label: t('menu.pdfToWord'), icon: () => h(SvgIcon, { icon: 'ri:file-pdf-2-line', size: '20' }), key: 'pdf-to-word' },
    )

    return items
  }

  // Community section
  const getCommunityItems = () => {
    const items = []

    const groupHeader = getGroupHeader('menu.group.community', 'community-group')
    if (groupHeader)
      items.push(groupHeader)

    items.push(
      { label: t('menu.elearning'), icon: () => h(SvgIcon, { icon: 'ri:graduation-cap-line', size: '20' }), key: 'elearning' },
      {
        label: t('menu.helpCommunity'),
        icon: () => h(SvgIcon, { icon: 'ri:question-answer-line', size: '20' }),
        key: 'help-community',
        children: [
          {
            label: t('menu.userGuide'),
            icon: () => h(SvgIcon, { icon: 'ri:file-info-line', size: '20' }),
            key: 'user-guide',
          },
          {
            label: t('menu.forum'),
            icon: () => h(SvgIcon, { icon: 'ri:chat-1-line', size: '20' }),
            key: 'forum',
          },
          {
            label: t('menu.contactSupport'),
            icon: () => h(SvgIcon, { icon: 'ri:customer-service-2-line', size: '20' }),
            key: 'contact-support',
          },
        ],
      },
    )

    return items
  }

  // System Settings section for Super Admin
  const getSystemSettingsItems = () => {
    const items = []

    // Group header: CÀI ĐẶT HỆ THỐNG
    const groupHeader = getGroupHeader('menu.group.systemSettings', 'system-settings-group')
    if (groupHeader)
      items.push(groupHeader)

    // 1. Cấu hình tham số (Parameter Configuration)
    items.push({
      label: t('menu.parameterConfiguration'),
      icon: () => h(AppSvgEIcon, { name: 'element-3', size: 24 }),
      key: 'parameter-configuration',
    })

    // 2. Credit & người dùng (Credit & Users) - có submenu
    items.push({
      label: t('menu.creditAndUsers'),
      icon: () => h(AppSvgEIcon, { name: 'coin', size: 24 }),
      key: 'credit-and-users',
      children: [
        {
          label: t('settings.systemSettings.creditBonus'),
          key: 'credit-bonus',
        },
        {
          label: t('settings.systemSettings.systemUsers'),
          key: 'system-users',
        },
      ],
    })

    // 3. Phê duyệt (Approval) - có submenu
    items.push({
      label: t('menu.approval'),
      icon: () => h(AppSvgEIcon, { name: 'tick-circle', size: 24 }),
      key: 'approval',
      children: [
        {
          label: t('menu.workspace'),
          key: 'workspace-approve',
        },
        {
          label: t('menu.image'),
          key: 'image-approve',
        },
      ],
    })

    // 4. Thông báo (Notifications)
    items.push({
      label: t('menu.notifications'),
      icon: () => h(AppSvgEIcon, { name: 'notification-bing', size: 24 }),
      key: 'notification-config',
    })

    // 5. Model AI
    items.push({
      label: t('aiModel.title'),
      icon: () => h(AppSvgEIcon, { name: 'speedometer', size: 24 }),
      key: 'ai-model',
    })

    // 6. Hệ thống thanh toán (Payment System)
    items.push({
      label: t('menu.paymentSystem.title'),
      icon: () => h(AppSvgEIcon, { name: 'money-2', size: 24 }),
      key: 'api-service-configuration',
    })

    // 7. Thiết lập sách (Book Setup) - có submenu
    items.push({
      label: t('menu.bookSetup'),
      icon: () => h(AppSvgEIcon, { name: 'book', size: 24 }),
      key: 'book-setup',
      children: [
        {
          label: t('settings.userManagementDropdown.textbook'),
          key: 'classroom.system-settings.textbook',
        },
        {
          label: t('settings.userManagementDropdown.teacherBook'),
          key: 'classroom.system-settings.teacher-book',
        },
        {
          label: t('settings.userManagementDropdown.decreeCircular'),
          key: 'classroom.system-settings.decree-circular',
        },
      ],
    })

    // 8. Cấu hình công cụ (Tool Configuration)
    items.push({
      label: t('settings.userManagementDropdown.toolManagement'),
      icon: () => h(AppSvgEIcon, { name: 'setting', size: 24 }),
      key: 'classroom.system-settings.tool-management',
    })

    return items
  }

  const getAgentAndStoreItems = () => {
    const items = []

    if (isOwner.value
			|| (hasMenuPermission(RolePermission.FOLDER_VIEW)
				|| hasMenuPermission(RolePermission.KNOWLEDGE_VIEW)
				|| hasMenuPermission(RolePermission.TASK_VIEW))
    ) {
      const groupHeader = getGroupHeader('menu.group.agentStore', 'agent-group')
      if (groupHeader)
        items.push(groupHeader)
    }

    // Luôn hiển thị workspace menu nếu có quyền FOLDER.VIEW hoặc là owner
    if (userRole.value?.is_owner === true || hasMenuPermission(RolePermission.FOLDER_VIEW)) {
      items.push({
        label: () => h('div', {
          class: 'flex items-center justify-between w-full min-w-0',
          onClick: (e: Event) => {
            e.stopPropagation()
            router.push('/agent-management')
          },
        }, [
          h('span', {
            class: 'flex-1 min-w-0 truncate mr-2',
            title: workSpaceName.value ?? '',
          }, workSpaceName.value ?? ''),
          // Chỉ hiển thị button thêm folder nếu có quyền FOLDER.EDIT
          hasMenuPermission(RolePermission.FOLDER_EDIT) && h(NButton, {
            text: true,
            class: 'flex-shrink-0',
            onClick: (e: Event) => {
              e.stopPropagation()
              handleAdd()
            },
          }, () => h(SvgIcon, { icon: 'ri:add-circle-line', size: '20' })),
        ]),
        icon: () => h(AppSvgEIcon, { name: 'element-4', size: 24 }),
        key: 'workspace',
        children: listFolder.value.map((folder: any) => ({
          label: () => h('div', { class: 'flex items-center justify-between w-full group min-w-0' }, [
            h('div', { class: 'flex items-center gap-2 flex-1 min-w-0' }, [
              h(SvgIcon, { icon: 'ri:folder-line', size: '20', class: 'flex-shrink-0' }),
              h('span', {
                class: 'truncate',
                title: folder.name,
              }, folder.name),
            ]),
            h('div', { class: 'flex items-center flex-shrink-0' }, [
              folder.isPinned && h('div', { class: 'w-[20px] h-[20px] flex justify-center items-center' }, [
                h(SvgIcon, { icon: 'ri:bookmark-3-line', size: '18' }),
              ]),
              // Chỉ hiển thị ActionFolder nếu có quyền FOLDER.EDIT hoặc là owner
              (isOwner.value || hasFolder(RolePermission.FOLDER_EDIT, folder.id)) && h('div', {
                class: folderDropdownStates.value[folder.id] ? 'block' : 'hidden group-hover:block',
                onClick: (e: Event) => e.stopPropagation(),
              }, [
                h(ActionFolder, {
                  isOrder: folder.isPinned,
                  uuid: folder.id,
                  showDelete: !folder?.folder_default_classroom_agent,
                  onDropdownChange: (isOpen: boolean) => handleDropdownChange(folder.id, isOpen),
                }),
              ]),
            ]),
          ]),
          key: `folder-${folder.id}`,
        })),
      })
    }

    // Knowledge menu - kiểm tra permission KNOWLEDGE.VIEW
    if (userRole.value?.is_owner === true || hasMenuPermission(RolePermission.KNOWLEDGE_VIEW))
      items.push({ label: t('menu.knowledges'), icon: () => h(AppSvgEIcon, { name: 'book', size: 24 }), key: 'knowledge' })

    // Tasks menu - kiểm tra permission TASK.VIEW
    if (userRole.value?.is_owner === true || hasMenuPermission(RolePermission.TASK_VIEW))
      items.push({ label: t('menu.tasks'), icon: () => h(AppSvgEIcon, { name: 'briefcase-tick', size: 24 }), key: 'tasks' })

    // Store

    items.push({
      label: t('menu.group.store'),
      icon: () => h(AppSvgEIcon, { name: 'shop', size: 24 }),
      key: 'store',
      children: [
        {
          label: t('menu.group.store'),
          key: 'agent-store',
        },
        {
          label: t('menu.uploadManagement'),
          key: 'transaction',
        },
      ],
    })
    return items
  }

  // Other section for Super Admin (KHÁC)
  const getOtherItems = () => {
    const items = []

    // Group header: KHÁC
    const groupHeader = getGroupHeader('menu.group.other', 'other-group')
    if (groupHeader)
      items.push(groupHeader)

    // 1. Công cụ (Tools) - có submenu
    items.push({
      label: t('menu.group.tool'),
      icon: () => h(AppSvgEIcon, { name: 'element-plus', size: 24 }),
      key: 'tools',
      children: [
        {
          label: t('menu.slide'),
          key: 'slide',
        },
        {
          label: t('menu.writer'),
          key: 'writer',
        },
        {
          label: t('menu.whiteboard'),
          key: 'Whiteboard',
        },
        {
          label: t('menu.chatPdf'),
          key: 'chat-pdf',
        },
        {
          label: t('menu.imageGeneration'),
          key: 'image-generation',
        },
        {
          label: t('menu.mindmap'),
          key: 'mind-map',
        },
        {
          label: t('menu.imageToWord'),
          key: 'image-to-word',
        },
        {
          label: t('menu.pdfToWord'),
          key: 'pdf-to-word',
        },
      ],
    })

    // 2. Elearning
    items.push({
      label: t('menu.elearning'),
      icon: () => h(AppSvgEIcon, { name: 'book-search', size: 24 }),
      key: 'elearning',
    })

    return items
  }

  return {
    expandedKeys,
    getChatMenuItem,
    getDivider,
    getAgentItems,
    getBaseToolItems,
    getClassroomItems,
    getCommunityItems,
    getStoreItems,
    getSystemSettingsItems,
    getAgentAndStoreItems,
    getOtherItems,
  }
}
export function useMenuItemsTeacher(_collapsed: any) {
  const route = useRoute()
  const router = useRouter()
  const classroomStore = useClassroomStore()
  const userStore = useUserStore()
  const {
    selectedKeyClassroom,
  } = useNavigation()

  const userRole = computed(() => userStore.role)

  // Lấy danh sách classroom từ store
  const classrooms = computed(() => classroomStore.getClassrooms)
  const currentClassroom = computed(() => classroomStore.getCurrentClassroom)
  const expandedKeys = computed(() => {
    const keys = getExpandedMenuKeysTeacher(route, classrooms.value)
    // Nếu có lớp được chọn, tự động expand menu lớp đó
    if (currentClassroom.value)
      keys.push('current-classroom')

    return keys
  })
  const { isRole } = useAuthz(userRole)
  const isAdmin = isRole('ADMIN')
  const isTeacher = isRole('TEACHER')

  const getClassroomItemsTeacher = () => {
    const items = []

    // Nếu có lớp được chọn, hiển thị lớp và các chức năng con
    if (currentClassroom.value && isTeacher) {
      items.push({
        label: () => h('div', {
          class: 'flex items-center justify-between w-full',
        }, [
          h('div', { class: 'flex items-center gap-2 flex-1 min-w-0 pr-2' }, [
            h('span', { class: 'truncate font-medium', title: currentClassroom.value?.name || `Classroom ${currentClassroom.value?.id}` }, currentClassroom.value?.name || `Classroom ${currentClassroom.value?.id}`),
          ]),
          h(NPopover, {
            trigger: 'click',
            placement: 'bottom',
            showArrow: false,
            class: '!p-0 rounded-2xl',
            contentClass: '!p-0',
            onClickoutside: (e: Event) => e.stopPropagation(),
          }, {
            trigger: () => h('div', {
              class: 'flex items-center cursor-pointer hover:bg-gray-100 dark:hover:bg-[#33354a] flex-shrink-0',
              onClick: (e: Event) => {
                e.stopPropagation()
              },
            }, [
              h(AppSvgEIcon, { name: 'refresh', size: 24 }),
            ]),
            default: () => h('div', {
              class: 'shadow-lg max-w-[180px]',
              onClick: (e: Event) => e.stopPropagation(),
            }, [
              ...classrooms.value.map((c: any) => h('div', {
                class: `px-4 py-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-[#33354a] ${c.id === currentClassroom.value?.id ? 'text-violet-500 font-medium' : ''}`,
                onClick: () => {
                  classroomStore.setCurrentClassroom(c)
                  router.push({ name: 'classroom.teacher.detail', params: { classroomId: c.id } })
                },
              }, c.name || `Classroom ${c.id}`)),
            ]),
          }),
        ]),

        icon: () => h(AppSvgEIcon, { name: 'menu', size: 24 }),
        key: 'current-classroom',
        children: [
          {
            label: () => h('div', {
              class: 'flex items-center gap-2 cursor-pointer',
              onClick: () => {
                router.push({
                  name: 'classroom.teacher.subject',
                  params: { classroomId: currentClassroom.value?.id },
                })
              },
            }, [
              h('span', t('menu.classroomSpace')),
            ]),
            key: 'classroom.teacher.subject',
          },
          {
            label: () => h('div', {
              class: 'flex items-center gap-2 cursor-pointer',
              onClick: () => {
                router.push({
                  name: 'classroom.teacher.member',
                  params: { classroomId: currentClassroom.value?.id },
                })
              },
            }, [
              h('span', t('menu.classMembers')),
            ]),
            key: 'classroom.teacher.member',
          },
          {
            label: () => h('div', {
              class: 'flex items-center gap-2 cursor-pointer',
              onClick: () => {
                router.push({
                  name: 'classroom.teacher.result',
                  params: { classroomId: currentClassroom.value?.id },
                })
              },
            }, [
              h('span', t('menu.classroomResult')),
            ]),
            key: 'classroom.teacher.result',
          },
        ],
      })
    }
    else {
      // Nếu chưa chọn lớp, hiển thị danh sách lớp để chọn

    }
    if (isAdmin) {
      items.push({
        label: t('menu.classroomList'),
        icon: () => h(AppSvgEIcon, { name: 'menu', size: 24 }),
        key: 'classroom-manage',
        children: isAdmin
          ? undefined
          : classrooms.value.map((classroom: any) => ({
            label: () => h('div', {
              class: 'flex items-center justify-between w-full cursor-pointer',
              onClick: () => {
                classroomStore.setCurrentClassroom(classroom)
                router.push({
                  name: 'classroom.teacher.detail',
                  params: { classroomId: classroom.id },
                })
              },
            }, [
              h('div', { class: 'flex items-center gap-2' }, [
                h('span', { class: 'truncate' }, classroom.name || `Classroom ${classroom.id}`),
              ]),
            ]),
            key: `classroom-${classroom.id}`,
          })),
      })
    }
    // Thêm các menu item khác
    items.push(
      {
        label: t('menu.classroomExamManage'),
        icon: () => h(AppSvgEIcon, { name: 'exam', size: 24 }),
        key: 'exam-list',
        children: [
          {
            label: t('menu.classroomExamPaper'),
            key: 'classroom.teacher.exam-paper',
          },
          {
            label: t('menu.classroomExam'),
            key: 'classroom.teacher.exam',
          },
        ],
      },
      { label: t('menu.classroomQuestionLibrary'), icon: () => h(AppSvgEIcon, { name: 'checklist', size: 24 }), key: 'classroom.teacher.question-bank' },
      // { label: t('menu.classAI'), icon: () => h(AppSvgEIcon, { name: 'cast_for_education', size: 24 }), key: 'classroom.teacher.start-lesson' },
      { label: t('menu.aiElearning'), icon: () => h(AppSvgEIcon, { name: 'book-search', size: 24 }), key: 'classroom.teacher.elearning' },
      { label: t('menu.classroomLearningResources'), icon: () => h(AppSvgEIcon, { name: 'store-03', size: 24 }), key: 'classroom.teacher.learning-resources' },
    )

    if (isTeacher || isAdmin) {
      const item = {
        label: t('menu.classroomTeachBoard'),
        // icon: () => h(AppSvgEIcon, { name: 'keyboard-open', size: 24 }),
        icon: () => h(AppSvgEIcon, { name: selectedKeyClassroom.value === 'classroom.teacher.board' ? 'flame-filled' : 'flame', size: 24 }),
        key: 'classroom.teacher.board',
      }
      const itemAssistant = {
        label: t('classroom.educationalAssistant'),
        // icon: () => h(AppSvgEIcon, { name: 'keyboard-open', size: 24 }),
        icon: () => h(SvgIcon, { icon: 'fluent:agents-16-regular', size: '24' }),
        key: 'classroom.teacher.agent',
      }

      items.splice(0, 0, item) // Đưa lên vị trí đầu tiên
      items.splice(1, 0, itemAssistant) // Đưa lên vị trí đầu tiên
    }

    return items
  }
  return {
    expandedKeys,
    getClassroomItemsTeacher,
  }
}

export function useMenuItemsStudent(_collapsed: any) {
  const route = useRoute()
  const router = useRouter()
  const classroomStore = useClassroomStore()
  const userStore = useUserStore()
  const favoritesStore = useFavoritesStore()
  const {
    selectedKeyClassroom,
  } = useNavigation()

  // Lấy danh sách classroom từ store
  const detailClassroom = computed(() => classroomStore.getCurrentClassroom)
  const listSubjectPin = computed(() => favoritesStore.getFavorites)

  const expandedKeys = computed(() => {
    return getExpandedMenuKeysStudent(route, listSubjectPin.value)
  })
  const getClassroomItemsStudent = () => {
    const items = []

    items.push(
      {
        label: t('menu.classroomLearningHub'),
        // icon: () => h(AppSvgEIcon, { name: 'keyboard-open', size: 24 }),
        icon: () => h(AppSvgEIcon, { name: selectedKeyClassroom.value === 'classroom.student.board' ? 'lightning-filled' : 'lightning', size: 24 }),
        key: 'classroom.student.board',
      },
      {
        label: t('classroom.educationalAssistant'),
        // icon: () => h(AppSvgEIcon, { name: 'keyboard-open', size: 24 }),
        icon: () => h(SvgIcon, { icon: 'fluent:agents-16-regular', size: '24' }),
        key: 'classroom.student.agent',
      },
      {
        label: () => h('div', {
          class: 'flex items-center gap-2 cursor-pointer',
          onClick: () => {
            router.push({
              name: 'classroom.student.examTab',
              params: { classroomId: detailClassroom.value?.id },
            })
          },
        }, [

          h('span', t('menu.classroomAssessments')),
        ]),
        icon: () => h(SvgIcon, { icon: 'streamline-flex:hand-held-tablet-writing', size: '20' }),
        key: 'classroom.student.examTab',
      },
      {
        label: () => h('div', {
          class: 'flex items-center gap-2 cursor-pointer',
          onClick: () => {
            router.push({
              name: 'classroom.student.document',
              params: {
                classroomId: detailClassroom.value?.id,
              },
            })
          },
        }, [
          // h(SvgIcon, { icon: 'ri:file-text-line', size: '20' }),
          h('span', t('menu.classroomDocument')),
        ]),
        icon: () => h(AppSvgEIcon, { name: 'notebook', size: 24 }),
        key: 'classroom.student.document',
      },
      {
        label: () => h('div', {
          class: 'flex items-center gap-2 cursor-pointer',
          onClick: () => {
            router.push({
              name: 'classroom.student.flashcard',
              params: {
                classroomId: detailClassroom.value?.id,
              },
            })
          },
        }, [
          // h(SvgIcon, { icon: 'mdi:cards-outline', size: '20' }),
          h('span', t('menu.classroomFlashcard')),
        ]),
        icon: () => h(SvgIcon, { icon: 'mdi:cards-outline', size: '20' }),
        key: 'classroom.student.flashcard',
      },
      {
        label: () => h('div', {
          class: 'flex items-center gap-2 cursor-pointer',
          onClick: () => {
            router.push({
              name: 'classroom.student.practice',
              params: {
                classroomId: detailClassroom.value?.id,
              },
            })
          },
        }, [
          // h(SvgIcon, { icon: 'ri:fire-line', size: '20' }),
          h('span', t('menu.classroomExercise')),
        ]),
        icon: () => h(AppSvgEIcon, { name: 'flame', size: 24 }),
        key: 'classroom.student.practice',
      },
      {
        label: () => h('div', {
          class: 'flex items-center gap-2 cursor-pointer',
          onClick: () => {
            router.push({
              name: 'classroom.student.exercise',
              params: {
                classroomId: detailClassroom.value?.id,
              },
            })
          },
        }, [
          // h(SvgIcon, { icon: 'ri:edit-2-line', size: '20' }),
          h('span', t('menu.classroomAssignments')),
        ]),
        icon: () => h(AppSvgEIcon, { name: 'edit-square', size: 24 }),
        key: 'classroom.student.exercise',
      },
      {
        label: () => h('div', {
          class: 'flex items-center gap-2 cursor-pointer',
          onClick: () => {
            router.push({
              name: 'classroom.student.result.detail',
              params: { classroomId: detailClassroom.value?.id, studentId: userStore.userInfo.userId },
            })
          },
        }, [
          // h(SvgIcon, { icon: 'ri:focus-2-line', size: '20' }),
          h('span', t('menu.classroomLearningOutcomes')),
        ]),
        icon: () => h(AppSvgEIcon, { name: 'target-arrow', size: 24 }),
        key: 'classroom.student.result.detail',
      },
      {
        type: 'divider',
        key: 'divider-1',
      },
      {
        label: () => h('div', {
          class: 'flex items-center gap-2 cursor-pointer',
          onClick: () => {
            router.push({
              name: 'classroom.student.elearning',
              params: { classroomId: detailClassroom.value?.id },
            })
          },
        }, [
          h('span', t('menu.aiElearning')),
        ]),
        icon: () => h(AppSvgEIcon, { name: 'book-search', size: 24 }),
        key: 'classroom.student.elearning',
      },
    )
    if (detailClassroom.value?.id) {
      const item = {
        label: () => h('div', {
          class: 'flex items-center gap-2 font-semibold uppercase',
        }, [
          // h(SvgIcon, { icon: 'ri:menu-line', size: '20' }),
          h('span', detailClassroom.value?.name || ''),
          h('span', { class: 'flex-1 h-[1px] bg-[#E0E0E0]' }),
        ]),
        // icon: () => h(SvgIcon, { icon: 'ri:menu-line', size: '20' }),
        key: 'classroom.student.detail',
        // children: listSubjectPin.value?.length
        //   ? listSubjectPin.value.map((subject: any) => ({
        //     label: () => h('div', {
        //       class: 'flex items-center justify-between w-full cursor-pointer',
        //       onClick: () => {
        //         router.push({
        //           name: 'classroom.student.subject.detail',
        //           params: { classroomId: detailClassroom.value?.id, subjectId: subject.id },
        //         })
        //       },
        //     }, [
        //       h('div', { class: 'flex items-center gap-2' }, [
        //         h('span', { class: 'truncate' }, subject.name || `Classroom ${subject.id}`),
        //       ]),
        //     ]),
        //     key: `subject-${subject.id}`,
        //   }))
        //   : undefined,
      }
      items.splice(2, 0, item)
    }

    return items
  }
  return {
    expandedKeys,
    getClassroomItemsStudent,
  }
}
